import { useEffect, useState } from "react";
import { Globe, Smartphone, Monitor, Download, ShieldCheck, RefreshCw, Users, Apple, Check, ArrowRight } from "lucide-react";
import { api } from "../api";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import "./download.css";

const isAndroid = () => /android/i.test(navigator.userAgent);
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

function Soon({ testid }) {
  return <span className="dl-soon" data-testid={testid}>Coming soon</span>;
}

function PlatformCard({ icon: Icon, title, sub, children, testid, highlight }) {
  return (
    <article className={`dl-card ${highlight ? "hl" : ""}`} data-testid={testid}>
      <div className="dl-ico"><Icon size={22} /></div>
      <h3>{title}</h3>
      <p>{sub}</p>
      <div className="dl-actions">{children}</div>
    </article>
  );
}

export default function DownloadPage() {
  const [d, setD] = useState({});
  const [installMsg, setInstallMsg] = useState("");
  const { canInstall, installed, install } = useInstallPrompt();
  useEffect(() => { api.get("/public/app-downloads").then((r) => setD(r.data)).catch(() => {}); }, []);
  const doInstall = async () => {
    if (await install()) setInstallMsg("Installed — find FynDine on your home screen or Start menu.");
    else if (!canInstall) setInstallMsg(isIOS() ? "On iPhone/iPad: tap Share, then 'Add to Home Screen'." : "Open this page in Chrome or Edge, then use the browser menu → 'Install FynDine'.");
  };
  return (
    <div className="dl-shell" data-testid="download-page">
      <header className="dl-top">
        <a href="/" className="dl-brand" data-testid="download-home-link"><img src="/icons/icon-192.png" alt="" /> <b>FynDine</b></a>
        <a href="/" className="dl-open" data-testid="download-open-web">Open web app <ArrowRight size={14} /></a>
      </header>
      <section className="dl-hero">
        <span className="dl-over">GET FYNDINE · WEB · ANDROID · WINDOWS</span>
        <h1>One restaurant OS, on every counter.</h1>
        <p>Use FynDine in the browser, install it on your phone, or run it on the billing PC. Same login, same subscription, same live data everywhere.</p>
      </section>
      <section className="dl-grid">
        <PlatformCard icon={Globe} title="Web app" sub="Works in Chrome, Edge, Safari and Firefox. Nothing to install." testid="dl-card-web" highlight={!isAndroid() && !isIOS()}>
          <a className="dl-btn" href="/" data-testid="dl-web-open">Open FynDine</a>
          {installed ? <span className="dl-note" data-testid="dl-installed"><Check size={13} /> Running as installed app</span>
            : <button className="dl-btn ghost" onClick={doInstall} data-testid="dl-install-button"><Download size={14} /> Install as app</button>}
        </PlatformCard>
        <PlatformCard icon={Smartphone} title="Android" sub="For captains, managers and owners on the move. Android 8 or later." testid="dl-card-android" highlight={isAndroid()}>
          {d.android_play_url ? <a className="dl-btn" href={d.android_play_url} target="_blank" rel="noreferrer" data-testid="dl-android-play">Get it on Google Play</a> : <Soon testid="dl-android-play-soon" />}
          {d.android_apk_url ? <a className="dl-btn ghost" href={d.android_apk_url} data-testid="dl-android-apk"><Download size={14} /> Download APK{d.android_version ? ` · v${d.android_version}` : ""}</a> : null}
        </PlatformCard>
        <PlatformCard icon={Monitor} title="Windows" sub="For the billing counter and kitchen PC, with direct thermal printing. Windows 10 or 11." testid="dl-card-windows">
          {d.windows_exe_url ? <a className="dl-btn" href={d.windows_exe_url} data-testid="dl-windows-exe"><Download size={14} /> Download for Windows{d.windows_version ? ` · v${d.windows_version}` : ""}</a> : <Soon testid="dl-windows-soon" />}
          {d.windows_exe_url && <span className="dl-note">If Windows SmartScreen appears, choose "More info" → "Run anyway".</span>}
        </PlatformCard>
        <PlatformCard icon={Apple} title="iPhone & iPad" sub="Open fyndine.in in Safari, tap Share, then 'Add to Home Screen'." testid="dl-card-ios" highlight={isIOS()}>
          <a className="dl-btn ghost" href="/" data-testid="dl-ios-open">Open in Safari</a>
        </PlatformCard>
      </section>
      {installMsg && <div className="dl-msg" data-testid="dl-install-message">{installMsg}</div>}
      {d.release_notes && <section className="dl-notes" data-testid="dl-release-notes"><h4>What's new</h4><p>{d.release_notes}</p></section>}
      <section className="dl-why">
        <div><Users size={18} /><b>Same account everywhere</b><span>Sign in with the same email or staff login. Branches, roles and permissions follow you.</span></div>
        <div><RefreshCw size={18} /><b>Always up to date</b><span>Orders, KOTs, stock and bills sync live across devices. New features arrive without reinstalling.</span></div>
        <div><ShieldCheck size={18} /><b>Secure by design</b><span>Your data stays on FynDine's servers, isolated per business. The apps only open fyndine.in over HTTPS.</span></div>
      </section>
      <footer className="dl-foot">Subscriptions are managed on the website. © VSM Transnational Pvt Ltd</footer>
    </div>
  );
}
